import { ChangeDetectionStrategy, Component, HostListener, Injector, Input, OnInit } from '@angular/core';
import {
  AntipatternStore,
  AppView,
  AssetsStore,
  StudentStore,
  UserStore,
  ViewStore,
} from '@coach-bot/data-access/shared';
import { getAssetsPath } from '@environment';
import { Language, initializeTranslations } from '@shared/translate';
import { AbstractComponent } from '@shared/ui';
import { BehaviorSubject, Observable, combineLatest, filter, map, take, takeUntil } from 'rxjs';
import { TRANSLATIONS, TRANSLATIONS_VERSION } from '../../../../../translations';
import { AppSubscriptionsService } from '../../services';

const BUTTON_TAG_NAME = 'BUTTON';
const INPUT_TAG_NAME = 'INPUT';

@Component({
  selector: 'da-extension',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent extends AbstractComponent implements OnInit {
  @Input() assets: string;

  currentView$: BehaviorSubject<AppView> = this.viewStore.currentNotificationView$;
  storesInitialized$: Observable<boolean>;
  translationsInitialized$: BehaviorSubject<boolean> = this.viewStore.translationsInitialized$;

  AppView = AppView;

  constructor(
    private appSubscriptionsService: AppSubscriptionsService,
    private antipatternStore: AntipatternStore,
    private assetsStore: AssetsStore,
    private injector: Injector,
    private studentStore: StudentStore,
    private userStore: UserStore,
    private viewStore: ViewStore
  ) {
    super();
    initializeTranslations(this.injector, getAssetsPath(), TRANSLATIONS_VERSION, TRANSLATIONS).then(
      (language: Language) => {
        this.viewStore.setCurrentLanguage(language);
        this.viewStore.setTranslationsInitialized(true);
      }
    );
  }

  ngOnInit(): void {
    this.assets = this.assets || getAssetsPath() || 'assets';
    this.assetsStore.setAssetsPath(this.assets);

    this.storesInitialized$ = combineLatest([
      this.antipatternStore.initialized$,
      this.assetsStore.initialized$,
      this.studentStore.initialized$,
      this.userStore.initialized$,
      this.viewStore.initialized$,
    ]).pipe(
      takeUntil(this.destroyed$),
      map((storesInitialized: boolean[]) => storesInitialized.every((item: boolean) => item))
    );
  }

  ngAfterViewInit(): void {
    this.storesInitialized$
      .pipe(
        filter((initialized: boolean) => initialized),
        take(1)
      )
      .subscribe(() => {
        this.appSubscriptionsService.subscribe();
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.appSubscriptionsService.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    // Check if the event target is the submit button or an input inside the form
    const isFormSubmission =
      event.target instanceof HTMLElement &&
      (
        (event.target.tagName === BUTTON_TAG_NAME && (event.target as HTMLButtonElement).type === 'submit') ||
        (event.target.tagName === INPUT_TAG_NAME && (event.target as HTMLInputElement).form)
      ) &&
      (event.target as Element).closest('#popup-layout');

    // Return if it's a form submission inside extension UI
    if (isFormSubmission) {
      return;
    }

    const popupLayoutElement = document.getElementById('popup-layout') || document.getElementById('antipattern-popup');
    if (popupLayoutElement) {
      const rect = popupLayoutElement.getBoundingClientRect();
      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      ) {
        this.viewStore.setNotificationView(null);
      }
    }
  }
}
